import { useMemo } from 'react';

export interface ShopMarker {
  id: string | number;
  name: string;
  category: string;
  color: string;
  lat: number;
  lng: number;
}

interface NaraMapProps {
  shops: ShopMarker[];
  selectedShop: ShopMarker | null;
  onSelectShop: (shop: ShopMarker | null) => void;
}

// Simplified Nara prefecture outline coordinates (screen-space)
// Derived from GeoJSON but simplified to geometric/angular style for CRXIS brand
const NARA_OUTLINE = [
  { x: 148, y: 18 },
  { x: 185, y: 22 },
  { x: 218, y: 35 },
  { x: 248, y: 28 },
  { x: 272, y: 42 },
  { x: 290, y: 68 },
  { x: 305, y: 55 },
  { x: 328, y: 72 },
  { x: 340, y: 98 },
  { x: 348, y: 130 },
  { x: 355, y: 165 },
  { x: 342, y: 200 },
  { x: 350, y: 240 },
  { x: 338, y: 280 },
  { x: 320, y: 318 },
  { x: 295, y: 350 },
  { x: 268, y: 378 },
  { x: 240, y: 398 },
  { x: 215, y: 418 },
  { x: 188, y: 430 },
  { x: 162, y: 420 },
  { x: 140, y: 395 },
  { x: 125, y: 365 },
  { x: 115, y: 330 },
  { x: 108, y: 295 },
  { x: 100, y: 258 },
  { x: 95, y: 220 },
  { x: 102, y: 182 },
  { x: 110, y: 148 },
  { x: 118, y: 115 },
  { x: 128, y: 82 },
  { x: 138, y: 50 },
  { x: 148, y: 18 },
];

// Convert GeoJSON lat/lng to screen coordinates within the map
function geoToScreen(lat: number, lng: number): { x: number; y: number } {
  const minLat = 33.82;
  const maxLat = 34.83;
  const minLng = 135.49;
  const maxLng = 136.27;

  const padding = 80;
  const width = 450 - padding * 2;
  const height = 450 - padding * 2;

  const x = padding + ((lng - minLng) / (maxLng - minLng)) * width;
  const y = padding + ((maxLat - lat) / (maxLat - minLat)) * height;

  return { x, y };
}

export function NaraMap({ shops, selectedShop, onSelectShop }: NaraMapProps) {
  const outlinePath = useMemo(() => {
    return NARA_OUTLINE.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`).join(' ') + ' Z';
  }, []);

  const shopPositions = useMemo(() => {
    return shops.map((shop) => ({
      ...shop,
      screen: geoToScreen(shop.lat, shop.lng),
    }));
  }, [shops]);

  return (
    <div className="relative w-full aspect-square max-w-[450px] mx-auto">
      {/* Glow background */}
      <div
        className="absolute inset-0 opacity-20 blur-3xl rounded-full"
        style={{ background: 'radial-gradient(circle, #00ff41 0%, transparent 70%)' }}
      />

      <svg
        viewBox="0 0 450 450"
        className="w-full h-full relative z-10"
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <filter id="glow">
            <feGaussianBlur stdDeviation="3" result="coloredBlur" />
            <feMerge>
              <feMergeNode in="coloredBlur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          <filter id="glow-strong">
            <feGaussianBlur stdDeviation="6" result="coloredBlur" />
            <feMerge>
              <feMergeNode in="coloredBlur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          <linearGradient id="outlineGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#00ff41" stopOpacity="0.8" />
            <stop offset="50%" stopColor="#00d9ff" stopOpacity="0.6" />
            <stop offset="100%" stopColor="#00ff41" stopOpacity="0.8" />
          </linearGradient>
          <pattern id="gridPattern" width="25" height="25" patternUnits="userSpaceOnUse">
            <path d="M 25 0 L 0 0 0 25" fill="none" stroke="#ffffff" strokeWidth="0.3" strokeOpacity="0.08" />
          </pattern>
        </defs>

        {/* Grid inside prefecture */}
        <clipPath id="naraClip">
          <path d={outlinePath} />
        </clipPath>
        <rect width="450" height="450" fill="url(#gridPattern)" clipPath="url(#naraClip)" />

        {/* Prefecture fill */}
        <path
          d={outlinePath}
          fill="rgba(0, 255, 65, 0.03)"
          stroke="none"
        />

        {/* Prefecture outline - glow layer */}
        <path
          d={outlinePath}
          fill="none"
          stroke="#00ff41"
          strokeWidth="1.5"
          strokeOpacity="0.3"
          filter="url(#glow-strong)"
        />

        {/* Prefecture outline - main */}
        <path
          d={outlinePath}
          fill="none"
          stroke="url(#outlineGrad)"
          strokeWidth="2"
          strokeLinejoin="bevel"
          strokeLinecap="round"
          filter="url(#glow)"
        />

        {/* Corner markers */}
        {NARA_OUTLINE.filter((_, i) => i % 4 === 0 && i < NARA_OUTLINE.length - 1).map((p, i) => (
          <circle
            key={`corner-${i}`}
            cx={p.x}
            cy={p.y}
            r="2"
            fill="#00ff41"
            opacity="0.4"
          />
        ))}

        {/* Shop markers */}
        {shopPositions.map((shop) => {
          const isSelected = selectedShop?.id === shop.id;
          return (
            <g
              key={shop.id}
              className="cursor-pointer"
              onClick={() => onSelectShop(isSelected ? null : shop)}
            >
              {/* Pulse ring */}
              <circle
                cx={shop.screen.x}
                cy={shop.screen.y}
                r={isSelected ? 18 : 12}
                fill="none"
                stroke={shop.color}
                strokeWidth="1"
                opacity={isSelected ? 0.6 : 0.3}
              >
                <animate
                  attributeName="r"
                  values={isSelected ? '14;22;14' : '8;14;8'}
                  dur="2s"
                  repeatCount="indefinite"
                />
                <animate
                  attributeName="opacity"
                  values={isSelected ? '0.6;0.1;0.6' : '0.3;0.05;0.3'}
                  dur="2s"
                  repeatCount="indefinite"
                />
              </circle>

              {/* Marker dot */}
              <circle
                cx={shop.screen.x}
                cy={shop.screen.y}
                r={isSelected ? 6 : 4}
                fill={shop.color}
                filter="url(#glow)"
                className="transition-all duration-300"
              />

              {/* Inner dot */}
              <circle
                cx={shop.screen.x}
                cy={shop.screen.y}
                r={isSelected ? 2.5 : 1.5}
                fill="#000"
              />

              {/* Label on hover/select */}
              {isSelected && (
                <g>
                  <rect
                    x={shop.screen.x + 12}
                    y={shop.screen.y - 14}
                    width={shop.name.length * 7.5 + 16}
                    height="24"
                    rx="4"
                    fill="rgba(0,0,0,0.85)"
                    stroke={shop.color}
                    strokeWidth="1"
                  />
                  <text
                    x={shop.screen.x + 20}
                    y={shop.screen.y + 2}
                    fill="#fff"
                    fontSize="11"
                    fontFamily="Bebas Neue, sans-serif"
                    letterSpacing="1"
                  >
                    {shop.name}
                  </text>
                </g>
              )}
            </g>
          );
        })}

        {/* Coordinate labels */}
        <text x="10" y="445" fill="#333" fontSize="9" fontFamily="monospace">
          135.49°E
        </text>
        <text x="380" y="445" fill="#333" fontSize="9" fontFamily="monospace">
          136.27°E
        </text>
        <text x="5" y="15" fill="#333" fontSize="9" fontFamily="monospace" transform="rotate(-90, 12, 80)">
          34.83°N
        </text>
      </svg>
    </div>
  );
}
